import {
  CursorState,
  ISO8601DateTimeString,
  ProjectId,
  SelectivePartial,
  UserId,
  UUIDv4,
} from "../types";
import { Asset } from "./assets";
import { Category } from "./categories";
import { PostBasedPaginatedResponse } from "./shared";
import { Status } from "./statusSets";

// Canonical System shape — System.categoryId uses Category["id"] (GUID),
// confirmed against the system table's category_id UUID NOT NULL column and
// BaseSystem.categoryId's format: uuid. Unlike Asset, System genuinely uses
// the GUID convention — preserve both, don't force one.
export type System = {
  id: UUIDv4;
  name: string;
  description?: string | null;
  categoryId: Category["id"];
  statusId: Status["id"];
  version: number;
  projectId: ProjectId;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  isActive: boolean;
  computedFields?: {
    parentSystemIds?: System["id"][];
  };
};

export type SystemDTO = Omit<
  System,
  "createdAt" | "updatedAt" | "deletedAt"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
};

export type SystemUpdate = Partial<
  Pick<System, "name" | "categoryId" | "statusId">
>;

export type SystemAttributeKey = keyof System;

export type SystemEditableAttributeKey = Extract<
  SystemAttributeKey,
  "name" | "description" | "categoryId" | "statusId"
>;

export type NewSystem = SelectivePartial<
  System,
  "statusId" | "categoryId",
  Exclude<SystemAttributeKey, SystemEditableAttributeKey>
>;

export type AssetOrSystem = Asset | System;
export type AssetOrSystemWithType = AssetOrSystem & {
  type: "system" | "asset";
};

export interface SystemQueryFilters {
  ids?: System["id"][];
  parentSystemIds?: System["id"][];
  ancestorSystemId?: System["id"][];
  names?: string[];
  categoryIds?: Category["id"][];
  assignedCategoryIds?: Category["id"][];
  statusIds?: Status["id"][];
  searchText?: string;
}

export interface SystemQueryRequest {
  limit?: number;
  cursorState?: CursorState;
  query: SystemQueryRequestBody;
  options: SystemQueryRequestOptions;
  orderBy?: SystemQuerySortOptions[];
}

// Superset: app's adds includeLastPageLink
export interface SystemQueryRequestOptions {
  includeDeleted?: boolean;
  includeTotalResults?: boolean;
  includeLastPageLink?: boolean;
  includedComputedFields?: string[];
  // Optional: no call site in systemsClient.ts provides this (it always sets
  // `options` at the top-level SystemQueryRequest, not nested in `query`).
  includedStandardFields?: string[];
}

// Superset: app's adds statusLabel/categoryNameHierarchy sort keys
export interface SystemQuerySortOptions {
  property: keyof System | "statusLabel" | "categoryNameHierarchy";
  direction: "asc" | "desc";
}

export interface SystemQueryRequestBody {
  entityType: "systems_atomic_query_v1";
  includeFilters?: SystemQueryFilters;
  excludeFilters?: SystemQueryFilters;
  // Optional: real callers set `options`/`orderBy` on the outer
  // SystemQueryRequest, not nested inside `query` — see SystemQueryRequest
  // below, which already declares its own `options`/`orderBy`.
  options?: SystemQueryRequestOptions;
  orderBy?: SystemQuerySortOptions;
}

// Wraps SystemDTO (raw), not a parsed System — the backend returns plain
// ISO8601 strings with no pre-parsed variant.
export type SystemQueryResponse = PostBasedPaginatedResponse<
  SystemDTO,
  SystemQueryRequest
>;

// Superset: app's adds description
export interface PatchSystemRequest {
  name?: System["name"];
  categoryId?: System["categoryId"];
  statusId?: System["statusId"];
  description?: System["description"];
}

export interface BatchPatchSystemRequest {
  [systemId: System["id"]]: PatchSystemRequest;
}

export type BatchSystemsModifyAssignmentsPayload =
  | ({
      entityType: "asset" | "system";
      entityId: Asset["id"] | System["id"];
    } & {
      addParentSystemIds: System["id"][] | Asset["id"][];
    })
  | {
      removeParentSystemIds: System["id"][] | Asset["id"][];
    };

// For reference picker use
export type SystemWithCategoryAndStatus = System & {
  category?: Category;
  status?: Status;
};

export const parseSystem = ({
  createdAt,
  updatedAt,
  deletedAt,
  ...system
}: SystemDTO): System => ({
  ...system,
  createdAt: new Date(createdAt),
  updatedAt: new Date(updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
});

type SystemComputedFields = "parentSystemIds";

export type SystemQueryOptions = {
  limit?: SystemQueryRequest["limit"];
  fetchAll?: boolean;
  includeFilters?: SystemQueryRequestBody["includeFilters"];
  cursorState?: SystemQueryRequest["cursorState"];
  includeDeleted?: boolean;
  includedComputedFields?: SystemComputedFields[];
};
